import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import {
  QrCode2, Timeline, Assessment, Business, LocationOn, Security,
  ViewInAr, Image, Psychology, Notifications, Dashboard, Description,
} from '@mui/icons-material';

const colors = {
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  slate: '#293b6d',
  frost: '#f5f7ff',
};

const features = [
  { icon: QrCode2, title: '스마트 QR/바코드', desc: '3초 만에 금형 확인' },
  { icon: Timeline, title: '실시간 수명 관리', desc: '고장 80% 감소' },
  { icon: Assessment, title: '생산이력 자동 기록', desc: '수기 기록 100% 절감' },
  { icon: Business, title: '고객사별 관리', desc: '대응 속도 3배 향상' },
  { icon: LocationOn, title: '위치 관리', desc: '찾기 시간 95% 단축' },
  { icon: Security, title: '권한별 관리', desc: '책임 소재 명확화' },
  { icon: ViewInAr, title: '3D 모델 뷰어', desc: '웹에서 바로 확인' },
  { icon: Image, title: '이미지 최적화', desc: '용량 90% 절약' },
  { icon: Psychology, title: 'AI 챗봇', desc: '24시간 상담 지원' },
  { icon: Notifications, title: '자동 알림', desc: '관리 누락 제로화' },
  { icon: Dashboard, title: '대시보드', desc: '의사결정 5배 향상' },
  { icon: Description, title: '리포트 자동 생성', desc: '작성 시간 100% 절감' },
];

const FeaturesSection: React.FC = () => {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, background: colors.midnight }}>
      <Container maxWidth="lg">
        <Stack spacing={2} alignItems="center" textAlign="center" mb={6}>
          <Typography variant="overline" sx={{ color: colors.cobaltBright, fontSize: '0.9rem', letterSpacing: '0.15em' }}>
            핵심 기능
          </Typography>
          <Typography variant="h2" sx={{ color: colors.frost, fontWeight: 700, fontSize: { xs: '2rem', md: '3rem' } }}>
            12가지 강력한 기능
          </Typography>
        </Stack>

        {/* Dashboard Preview Image */}
        <Box
          sx={{
            mb: 6,
            borderRadius: '20px',
            overflow: 'hidden',
            border: `1px solid ${colors.cobaltSky}30`,
            boxShadow: `0 20px 60px ${colors.cobaltBright}20`,
          }}
        >
          <img
            src="/landing/dashboard-preview.jpg"
            alt="EGMMS Dashboard Preview"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <Box key={idx} sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)', lg: 'calc(25% - 18px)' } }}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${colors.slate}40, ${colors.midnight})`,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${colors.cobaltSky}20`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      borderColor: colors.cobaltBright,
                      boxShadow: `0 15px 30px ${colors.cobaltBright}30`,
                    },
                  }}
                >
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${colors.cobaltBright}30, ${colors.cobaltSky}20)`,
                      }}
                    >
                      <Icon sx={{ fontSize: 28, color: colors.cobaltBright }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: colors.frost, fontSize: '1.1rem', fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.cobaltIce, fontSize: '0.9rem' }}>
                      {feature.desc}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
